import {
  Coord,
  Bounds,
} from '@t/LayoutTypes';

export enum FileStatus {
  Pending = "pending",
  Processing = "processing",
  Skipped = "skipped",
  Done = "done",
  Error = "error",
}

export type ImageFile = {
  file: File;
  name: string;
  // size: number;
  // file_type: string;
  type: string | null;
  eye: string | null;
  capture_date: string | null;
  study_id: string | null;
  patient_id: string | null;
  // blobURL?: string;
  checksum: string;
  // sub_images: any;
  // lastModified: number;
  // status: FileStatus;
};

export type RequiredImageTypes = { [type: string]: number };

// Populated from values fetched on startup (see appConfig.ts).
// The initial values are only a fallback for non-app code paths (tests, tooling);
// the running app always overwrites them via applyAppConfig() before render.
export const requiredImageTypes: RequiredImageTypes = {
  'OCT': 97,
  'FAF': 1,
  'FAFONH': 1,
  // 'RED',
  // 'OCT Properties': 1,
  // 'FAF Properties': 1,
  // 'FAFONH Properties': 1,
};

export const imageTypeKeywords = {
  'OCT': 'OCT',
  'FAF': 'FAF-2',
  'FAFONH': 'FAF-1M',
  'OCT Properties': 'OCT Properties',
  'FAF Properties': 'FAF-2 Properties',
  'FAFONH Properties': 'FAF-1M Properties',
};

export const ImageTypeToSubImages = {
  OCT: {
    RED: {
      x: 0,
      y: 0,
      width: 496,
      height: 496,
    },
    BSCAN: {
      x: 496,
      y: 0,
      width: undefined,
      height: 496,
    },
  },
  FAF: {
    FAF: {
      x: 0,
      y: 0,
      height: 768,
      width: 768,
    },
  },
  FAFONH: {
    FAF: {
      x: 0,
      y: 0,
      width: 768,
      height: 768,
    },
  },
};

export const imageDimensions = {
  BSCAN: { w: 1024, h: 496 },
  RED: { w: 496, h: 496 },
  FAF: { w: 768, h: 768 },
};

export type OctSettings = {
  enfaceAngle: number;
  patternSize: { w: number; h: number };
  totalBscans: number;
};

// Like requiredImageTypes, these hold appConfig values applied on startup.
// They are mutated in place (never reassigned) so that the module-level imports
// scattered across the SVG editors keep pointing at the live values.
export const octSettings: OctSettings = {
  enfaceAngle: 30,
  patternSize: { w: 20, h: 20 },
  totalBscans: 97,
};

// Derived from octSettings; recomputed whenever octSettings changes.
export const octMarginDeg: Coord = { x: 0, y: 0 };
export const octBoundsDeg: Bounds = { xMin: 0, xMax: 0, yMin: 0, yMax: 0 };

function recomputeOctDerived(): void {
  octMarginDeg.x = (octSettings.enfaceAngle - octSettings.patternSize.w) / 2;
  octMarginDeg.y = (octSettings.enfaceAngle - octSettings.patternSize.h) / 2;
  octBoundsDeg.xMin = octMarginDeg.x;
  octBoundsDeg.xMax = octSettings.enfaceAngle - octMarginDeg.x;
  octBoundsDeg.yMin = octMarginDeg.y;
  octBoundsDeg.yMax = octSettings.enfaceAngle - octMarginDeg.y;
}
recomputeOctDerived();

// Overwrites the module-level image/OCT defaults with values fetched from the
// server. Mutates the exported objects in place (rather than reassigning them)
// so existing `import { octSettings }`-style references stay valid. This must run
// before any consumer module — notably the SVG editors, which snapshot derived
// constants at import time — is evaluated. See src/index.tsx for the ordering.
export function applyAppConfig(next: {
  requiredImageTypes: RequiredImageTypes;
  octSettings: OctSettings;
}): void {
  for (const k of Object.keys(requiredImageTypes))
    delete requiredImageTypes[k];
  Object.assign(requiredImageTypes, next.requiredImageTypes);

  octSettings.enfaceAngle = next.octSettings.enfaceAngle;
  octSettings.patternSize = { ...next.octSettings.patternSize };
  octSettings.totalBscans = next.octSettings.totalBscans;
  recomputeOctDerived();
}

export function getPngDimensions(b: ArrayBuffer) : { width: number, height: number } {
  const dataView = new DataView(b);
  if (dataView.getUint32(0) !== 0x89504E47) {
    throw new Error("Not a PNG file");
  }
  const width = dataView.getUint32(16);
  const height = dataView.getUint32(20);
  return { width, height };
}

// includes files in subdirectories
export async function getDataTransferFiles(itemList: DataTransferItemList): Promise<FileSystemFileEntry[]> {
  const fileEntries: FileSystemFileEntry[] = [];
  const stack: FileSystemEntry[] = [];
  for (let i = 0; i < itemList.length; i++) {
    const listEntry = itemList[i].webkitGetAsEntry();
    if (listEntry)
      stack.push(listEntry);
  }
  while (stack.length > 0) {
    let entry = stack.pop()!;
    if (entry.isFile)
      fileEntries.push(entry as FileSystemFileEntry);
    else if (entry.isDirectory)
      stack.push(...await readAllEntries((entry as FileSystemDirectoryEntry).createReader()));
  }
  return fileEntries;
}

// loop because single read may not return all
async function readAllEntries(directoryReader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  const entries: FileSystemEntry[] = [];
  let readEntries = await readEntriesAsync(directoryReader);
  while (readEntries.length > 0) {
    entries.push(...readEntries);
    readEntries = await readEntriesAsync(directoryReader);
  }
  return entries;
}

// wrap the oldskool callback-based API in a promise
async function readEntriesAsync(directoryReader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return await new Promise((resolve, reject) => {
    directoryReader.readEntries(resolve, reject);
  });
}
